import React, { useEffect, useState } from 'react';
import { Table, TableHead, TableRow, TableCell, TableBody, Button, Typography } from '@mui/material';
import { COLOR_PRIMARY_2 } from 'src/utils/colors';
import { useUpgradeMesageForHosts } from 'src/hooks/useUpgradeText';
import { useSelector } from 'react-redux';
import { User } from 'src/interfaces/iuser';

const CTAButton = ({
  handleOnCLick,
  dashboardLayoutView,
  blurStartElement,
  blurEndElement,
  useHighlitedColor,
  onModalClose,
  user,
  handleUnlock,
}: {
  handleOnCLick: (type: string) => void;
  dashboardLayoutView: string;
  blurStartElement: Element | null;
  blurEndElement: Element | null;
  useHighlitedColor?: boolean;
  onModalClose?: () => void;
  user: User;
  handleUnlock: () => void;
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (blurStartElement && blurEndElement) {
      const startRect = blurStartElement.getBoundingClientRect();
      const endRect = blurEndElement.getBoundingClientRect();
      const tableRect = blurStartElement.closest('table')?.getBoundingClientRect();

      if (tableRect) {
        setPosition({
          top: startRect.top - tableRect.top,
          left: (startRect.left + endRect.right) / 2 - tableRect.left,
        });
      }
    }
  }, [blurStartElement, blurEndElement]);  

  const upgradePlanMessage = useUpgradeMesageForHosts(dashboardLayoutView);
  const isUserAllowdedToUnlock = ['expert', 'scale'].includes(user.subscriptionPlan);

  return (
    <Button
      sx={{
        position: 'absolute',
        background: 'transparent',
        fontSize: '20px',
        fontWeight: 500,
        color: COLOR_PRIMARY_2,
        cursor: 'pointer',
        pointerEvents: 'auto',
        textDecoration: 'underline',
        transform: 'translateX(-50%)',
        top: `${position.top}px`,
        left: `${position.left}px`,
        ...(useHighlitedColor
          ? {
              background: COLOR_PRIMARY_2,
              color: 'white',
              borderRadius: '5px',
              padding: '5px 10px',
              textDecoration: 'none',
              '&:hover': {
                background: COLOR_PRIMARY_2 + 'AA',
                color: 'white',
                textDecoration: 'underline',
              },
            }
          : {}),
      }}
      onClick={() => {
        isUserAllowdedToUnlock ? handleUnlock() : handleOnCLick('expert');
        onModalClose && onModalClose();
      }}
    >
      {!isUserAllowdedToUnlock ? upgradePlanMessage : 'Unlock'}
    </Button>
  );
};

const BasicTable = ({
  header,
  rows,
  blurredColumns,
  skipRowsFromBlur,
  onButtonClick,
  dashboardLayoutView,
  renderingFrom,
  onModalClose,
  handleUnlock,
}: {
  header: any[];
  rows: any[][];
  blurredColumns: number[];
  skipRowsFromBlur: number[];
  onButtonClick: (type: string) => void;
  dashboardLayoutView: string;
  renderingFrom?: 'dashboard' | 'modal';
  onModalClose?: () => void;
  handleUnlock: () => void;
}) => {
  const [blurStartElement, setBlurStartElement] = useState<Element | null>(null);
  const [blurEndElement, setBlurEndElement] = useState<Element | null>(null);

  const user = useSelector((state: any) => state.dashboard.user);

  useEffect(() => {
    const start = document.querySelector('.blur-cta-button-start-index');
    const end = document.querySelector('.blur-cta-button-end-index');
    setBlurStartElement(start);
    setBlurEndElement(end);
  }, [rows]);

  return (
    <Table size="small" sx={{ position: 'relative' }}>
      <TableHead>
        <TableRow>
          {header.map((column, index) => (
            <TableCell key={index}>{column}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody style={{ position: 'relative' }}>
        {rows.map((row, rowIndex) => (
          <TableRow
            key={rowIndex}
            style={{
              position: 'relative',
              backgroundColor: rowIndex % 2 === 0 ? '#f5f5f5' : 'white',
            }}
          >
            {row.map((cell, cellIndex) => {
              return (
                <TableCell
                  key={cellIndex}
                  className={
                    cellIndex > 0
                      ? rowIndex === 5 && cellIndex === blurredColumns[0]
                        ? 'notranslate blur-cta-button-start-index'
                        : rowIndex === 5 && cellIndex === row.length - 1
                        ? 'notranslate blur-cta-button-end-index'
                        : 'notranslate'
                      : ''
                  }
                  style={{
                    filter:
                      blurredColumns.includes(cellIndex) && !skipRowsFromBlur.includes(rowIndex)
                        ? 'blur(4px)'
                        : 'none',
                  }}
                >
                  {cell}
                </TableCell>
              );
            })}
          </TableRow>
        ))}

        {blurredColumns && blurStartElement && blurEndElement && (
          <CTAButton
            handleOnCLick={onButtonClick}
            dashboardLayoutView={dashboardLayoutView}
            blurStartElement={blurStartElement}
            blurEndElement={blurEndElement}
            useHighlitedColor={renderingFrom && renderingFrom === 'modal' ? true : false}
            onModalClose={onModalClose}
            user={user}
            handleUnlock={handleUnlock}
          />
        )}
      </TableBody>
    </Table>
  );
};

export default BasicTable;
